import { isEqual } from 'lodash-es';
import {
  CONTENT_TYPE_FIELD,
  CPS_ACTIONS_ALLOWED_FOR_DOCUMENT,
  CPS_NOTIFICATION_TYPE,
  CpsDocumentMetadata,
  CpsIndex,
  CpsLocale,
  CpsNotificationProps,
  CpsSearchExportRequest,
  CpsSelectedDocument,
  DeleteDocumentsRequest,
  DeleteDocumentsResponse,
  DOC_ID_FIELD,
  GetDocumentMetadataRequest,
  PERMISSIONS_FIELD,
  LockUnlockDocumentsInfoRequest,
  LockUnlockResponse,
  MAX_ITEMS_LIMIT_EXPORT_CSV,
  ResponseWithHeaders,
  TOO_MANY_REQUESTS_EXPORT_CSV
} from '@cs-cms/types';
import { ICpsDialogProps } from '../../../common/useCpsDialog';
import {
  ActionItemId,
  CONFIRM_DIALOG,
  ConfirmDialogType,
  EditedDocument,
  TooManyRequestsTimer
} from './FilterActions.types';
import {
  buildConfirmDialogMessage,
  countDocumentsByPermission,
  mapSelectedDocumentsToIdAndCt,
  showDeleteResultToast,
  showLockUnlockResultToast,
  TranslateFn
} from './filterActionsHelpers';
import { buildSimilarDocumentMetadata } from './buildSimilarDocumentMetadata';
import { PreviewDocument } from '../../documentPreview';

interface SearchExportContext {
  dataTotalCount: number | undefined;
  queryRequest:
    | Pick<
        CpsSearchExportRequest,
        'filters' | 'actionsFilter' | 'searchText' | 'searchFields' | 'orderBy' | 'orderByIsDescending'
      >
    | undefined;
  currentViewFieldNames: string[] | undefined;
  selectedLocale: CpsLocale;
  selectedView: number | undefined;
  handleTriggerExportToCsv: ((request: CpsSearchExportRequest) => void | Promise<void>) | undefined;
  tooManyRequestsTimer: TooManyRequestsTimer | undefined;
  setTooManyRequestsTimer: (timer: TooManyRequestsTimer | undefined) => void;
  setDialogVisible: (dialog: ConfirmDialogType | undefined) => void;
}

export interface FilterActionHandlerContext {
  translate: TranslateFn;
  showDialog: (props: ICpsDialogProps) => void;
  showToast: (options: Omit<CpsNotificationProps, 'show'>) => void;
  selectedDocuments: CpsSelectedDocument[] | undefined;
  selectedIndex: CpsIndex | undefined;
  downloadFiles: (() => void) | undefined;
  mutateLockDocuments: (
    params: { dto: LockUnlockDocumentsInfoRequest },
    options: { onSuccess: (response: LockUnlockResponse) => void }
  ) => void;
  mutateUnlockDocuments: (
    params: { dto: LockUnlockDocumentsInfoRequest },
    options: { onSuccess: (response: LockUnlockResponse) => void }
  ) => void;
  mutateDeleteDocuments: (
    params: { dto: DeleteDocumentsRequest },
    options: { onSuccess: (response: DeleteDocumentsResponse) => void }
  ) => void;
  openNewDocumentForm: (initialMetadata?: CpsDocumentMetadata, contentType?: string) => void;
  openEditDocumentForm: (document: EditedDocument) => void;
  openDocumentPreview: (document: PreviewDocument) => void;
  getDocumentMetadata: (
    dto: GetDocumentMetadataRequest
  ) => Promise<ResponseWithHeaders<{ metadata: CpsDocumentMetadata }>>;
  exportContext: SearchExportContext;
}

function showStandardConfirmDialog(
  context: FilterActionHandlerContext,
  titleKey: string,
  message: string,
  onConfirm: () => void | Promise<void>
): void {
  const { translate, showDialog } = context;
  showDialog({
    title: translate(titleKey),
    message,
    confirmButtonLabel: translate('custom.common.confirm'),
    discardButtonLabel: translate('custom.common.discard'),
    onConfirm: confirmed => {
      if (confirmed) {
        return onConfirm();
      }
      return undefined;
    }
  });
}

function handleDownloadAction(context: FilterActionHandlerContext): void {
  const { selectedDocuments, selectedIndex, downloadFiles } = context;
  if (!downloadFiles || !selectedDocuments || !selectedIndex) {
    return;
  }

  const counts = countDocumentsByPermission(selectedDocuments, CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD);
  const message = buildConfirmDialogMessage(
    context.translate,
    'custom.toolbar.actions.download.downloadDialogMessage',
    'custom.toolbar.actions.download.skippedDownloadDialogMessage',
    'custom.toolbar.actions.download.downloadProceed',
    counts
  );

  showStandardConfirmDialog(context, 'custom.toolbar.actions.download.downloadDialogTitle', message, () =>
    downloadFiles()
  );
}

function handleLockAction(context: FilterActionHandlerContext): void {
  const { selectedDocuments, selectedIndex, translate, mutateLockDocuments, showToast } = context;
  if (!selectedDocuments || !selectedIndex) {
    return;
  }

  const counts = countDocumentsByPermission(selectedDocuments, CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.LOCK);
  const message = buildConfirmDialogMessage(
    translate,
    'custom.toolbar.actions.lock.lockDialogMessage',
    'custom.toolbar.actions.lock.skippedLockDialogMessage',
    'custom.toolbar.actions.lock.lockProceed',
    counts
  );
  const dto: LockUnlockDocumentsInfoRequest = {
    documents: mapSelectedDocumentsToIdAndCt(selectedDocuments),
    indexId: selectedIndex.id
  };

  showStandardConfirmDialog(context, 'custom.toolbar.actions.lock.lockDialogTitle', message, () =>
    mutateLockDocuments(
      { dto },
      {
        onSuccess: response =>
          showLockUnlockResultToast(
            response,
            translate,
            showToast,
            'custom.toolbar.actions.lock.partialLock',
            'custom.toolbar.actions.lock.successfullLock'
          )
      }
    )
  );
}

function handleUnlockAction(context: FilterActionHandlerContext): void {
  const { selectedDocuments, selectedIndex, translate, mutateUnlockDocuments, showToast } = context;
  if (!selectedDocuments || !selectedIndex) {
    return;
  }

  const counts = countDocumentsByPermission(selectedDocuments, CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.LOCK);
  const message = buildConfirmDialogMessage(
    translate,
    'custom.toolbar.actions.lock.unlockDialogMessage',
    'custom.toolbar.actions.lock.skippedUnlockDialogMessage',
    'custom.toolbar.actions.lock.unlockProceed',
    counts
  );
  const dto: LockUnlockDocumentsInfoRequest = {
    documents: mapSelectedDocumentsToIdAndCt(selectedDocuments),
    indexId: selectedIndex.id
  };

  showStandardConfirmDialog(context, 'custom.toolbar.actions.lock.unlockDialogTitle', message, () =>
    mutateUnlockDocuments(
      { dto },
      {
        onSuccess: response =>
          showLockUnlockResultToast(
            response,
            translate,
            showToast,
            'custom.toolbar.actions.lock.partialUnlock',
            'custom.toolbar.actions.lock.successfullUnlock'
          )
      }
    )
  );
}

function handleDeleteAction(context: FilterActionHandlerContext): void {
  const { selectedDocuments, selectedIndex, translate, mutateDeleteDocuments, showToast } = context;
  if (!selectedDocuments || !selectedIndex) {
    return;
  }

  const counts = countDocumentsByPermission(selectedDocuments, CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.DELETE);
  const message = buildConfirmDialogMessage(
    translate,
    'custom.toolbar.actions.delete.deleteDialogMessage',
    'custom.toolbar.actions.delete.skippedDeleteDialogMessage',
    'custom.toolbar.actions.delete.deleteProceed',
    counts
  );
  const dto: DeleteDocumentsRequest = {
    documents: mapSelectedDocumentsToIdAndCt(selectedDocuments),
    indexId: selectedIndex.id
  };

  showStandardConfirmDialog(context, 'custom.toolbar.actions.delete.deleteDialogTitle', message, () =>
    mutateDeleteDocuments({ dto }, { onSuccess: response => showDeleteResultToast(response, translate, showToast) })
  );
}

function buildExportRequest(context: FilterActionHandlerContext): CpsSearchExportRequest {
  const { exportContext } = context;
  return {
    filters: exportContext.queryRequest?.filters,
    actionsFilter: exportContext.queryRequest?.actionsFilter,
    searchText: exportContext.queryRequest?.searchText,
    searchFields: exportContext.queryRequest?.searchFields,
    orderBy: exportContext.queryRequest?.orderBy,
    orderByIsDescending: exportContext.queryRequest?.orderByIsDescending,
    select: exportContext.currentViewFieldNames,
    locale: exportContext.selectedLocale,
    viewId: exportContext.selectedView
  };
}

function handleExportAction(context: FilterActionHandlerContext): void | Promise<void> {
  const { exportContext } = context;
  const { handleTriggerExportToCsv, dataTotalCount, tooManyRequestsTimer, setDialogVisible, setTooManyRequestsTimer } =
    exportContext;

  if (!handleTriggerExportToCsv) {
    return undefined;
  }

  if (dataTotalCount !== undefined && dataTotalCount > MAX_ITEMS_LIMIT_EXPORT_CSV) {
    setDialogVisible(CONFIRM_DIALOG.MAX_EXPORT_ITEMS);
    return undefined;
  }

  const querySearchExport = buildExportRequest(context);

  if (tooManyRequestsTimer && isEqual(querySearchExport, tooManyRequestsTimer.querySearchExport)) {
    setDialogVisible(CONFIRM_DIALOG.TOO_MANY_REQUESTS);
    return undefined;
  }

  setTooManyRequestsTimer({ querySearchExport, timeLeft: TOO_MANY_REQUESTS_EXPORT_CSV });
  return handleTriggerExportToCsv(querySearchExport);
}

function getSelectedEditedDocument(selectedDocuments: CpsSelectedDocument[]): EditedDocument | undefined {
  if (selectedDocuments.length !== 1) {
    return undefined;
  }

  const selectedDocument = selectedDocuments[0];
  return {
    [CONTENT_TYPE_FIELD]: selectedDocument[CONTENT_TYPE_FIELD],
    [DOC_ID_FIELD]: selectedDocument[DOC_ID_FIELD]
  };
}

function handleEditAction(context: FilterActionHandlerContext, selectedDocuments: CpsSelectedDocument[]): void {
  const editedDocument = getSelectedEditedDocument(selectedDocuments);
  if (!editedDocument) {
    return;
  }

  context.openEditDocumentForm(editedDocument);
}

function handleDisplayMetadataAction(
  context: FilterActionHandlerContext,
  selectedDocuments: CpsSelectedDocument[]
): void {
  if (selectedDocuments.length !== 1) {
    return;
  }

  const selectedDocument = selectedDocuments[0];
  if (!selectedDocument) {
    return;
  }

  context.openDocumentPreview({
    contentType: selectedDocument[CONTENT_TYPE_FIELD],
    documentId: selectedDocument[DOC_ID_FIELD],
    permissions: selectedDocument[PERMISSIONS_FIELD]
  });
}

async function handleCreateSimilarAction(
  context: FilterActionHandlerContext,
  selectedDocuments: CpsSelectedDocument[]
): Promise<void> {
  const editedDocument = getSelectedEditedDocument(selectedDocuments);
  if (!editedDocument || !context.selectedIndex) {
    return;
  }

  const contentType = editedDocument[CONTENT_TYPE_FIELD];
  const documentId = editedDocument[DOC_ID_FIELD];

  try {
    const response = await context.getDocumentMetadata({
      indexId: context.selectedIndex.id,
      ctName: contentType,
      documentId
    });
    const sanitizedMetadata = buildSimilarDocumentMetadata(response.data.metadata);
    context.openNewDocumentForm(sanitizedMetadata, contentType);
  } catch {
    context.showToast({
      type: CPS_NOTIFICATION_TYPE.ERROR,
      message: context.translate('custom.toolbar.actions.form.createSimilarFailed')
    });
  }
}

const actionHandlers: Partial<
  Record<
    ActionItemId,
    (context: FilterActionHandlerContext, selectedDocuments: CpsSelectedDocument[]) => void | Promise<void>
  >
> = {
  [ActionItemId.DOWNLOAD]: context => handleDownloadAction(context),
  [ActionItemId.LOCK]: context => handleLockAction(context),
  [ActionItemId.UNLOCK]: context => handleUnlockAction(context),
  [ActionItemId.DELETE]: context => handleDeleteAction(context),
  [ActionItemId.EXPORT]: context => handleExportAction(context),
  [ActionItemId.ADD_NEW]: context => {
    context.openNewDocumentForm();
    return undefined;
  },
  [ActionItemId.CREATE_SIMILAR]: (context, selectedDocuments) =>
    handleCreateSimilarAction(context, selectedDocuments),
  [ActionItemId.EDIT]: (context, selectedDocuments) => handleEditAction(context, selectedDocuments),
  [ActionItemId.DISPLAY_METADATA]: (context, selectedDocuments) =>
    handleDisplayMetadataAction(context, selectedDocuments)
};

export function handleFilterActionClick(
  actionId: ActionItemId,
  context: FilterActionHandlerContext,
  selectedDocuments: CpsSelectedDocument[]
): void | Promise<void> | undefined {
  const handler = actionHandlers[actionId];
  if (!handler) {
    return undefined;
  }
  return handler(context, selectedDocuments);
}
